import React from 'react'
import { OpportunityDetails, Investment } from '../../lib/types'
import { convertNumberWithCommas } from '../../lib/utils/helpers'
import {
  formatAssetClassLabel,
  formatInterestPercent,
  formatLockupFromOpportunity,
} from './invest-v2-utils'

type DealRecapProps = {
  opportunity: OpportunityDetails
  investmentData: Investment | undefined
  variant?: 'amount' | 'review' | 'sign' | 'fund'
}

const SecureEnvironmentIcon = () => (
  <svg
    className='invest-v2__shield-svg invest-v2__shield-svg--trust'
    width='40'
    height='40'
    viewBox='0 0 40 40'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden
  >
    <circle cx='20' cy='20' r='19' fill='#e8f4fc' stroke='#93c5fd' strokeWidth='1' />
    <path
      d='M20 11l-7 2.8v4.9c0 3.5 2.4 6.8 7 7.8 4.6-1 7-4.3 7-7.8v-4.9L20 11z'
      stroke='#114b8c'
      strokeWidth='1.4'
      strokeLinejoin='round'
      fill='none'
    />
    <path
      d='M17 19.5l2 2 4-4.5'
      stroke='#114b8c'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const InvestV2DealRecap = ({ opportunity, investmentData, variant = 'amount' }: DealRecapProps) => {
  const minTicket = opportunity.ticketSize ? parseInt(opportunity.ticketSize, 10) / 100 : 0
  const lockInDays = opportunity.assetClassData?.lock_in_period_in_days
  const hasLockup = Number(lockInDays) > 0
  const hasInterest = Number(String(opportunity.irr ?? '').replace(/[^0-9.-]/g, '')) !== 0
  const hasCommittedAmount = Boolean(investmentData?.amount)
  const displayAmount = hasCommittedAmount
    ? convertNumberWithCommas((Number(investmentData?.amount) / 100).toFixed(2))
    : null

  const infoMessage = () => {
    if (variant === 'review') {
      return 'Reviewing this summary is the final step before legal documentation. Ensure your personal details and investment amount are correct.'
    }
    if (variant === 'sign') {
      return 'This investment is subject to the terms in the subscription documents. Confirming below will finalize your legal commitment subject to funding.'
    }
    if (variant === 'fund') {
      return 'Pay from your wallet to place the order. If your balance is short, top up first; your allocation is secured once the order is placed.'
    }
    return 'Secured institutional-grade platform. All data is encrypted and handled according to GDPR standards.'
  }

  return (
    <aside className='invest-v2__recap' data-testid='invest-v2-deal-recap'>
      <div className='invest-v2__card invest-v2__recap-card'>
        <div className='invest-v2__recap-head'>
          <h2 className='invest-v2__recap-title'>Deal Recap</h2>
        </div>
        <p className='invest-v2__recap-category'>{formatAssetClassLabel(opportunity.assetClass)}</p>
        <h3 className='invest-v2__recap-deal'>{opportunity.projectName}</h3>
        <dl className='invest-v2__recap-rows'>
          {hasLockup && (
            <div className='invest-v2__recap-row'>
              <dt>Lock-in Period</dt>
              <dd>{formatLockupFromOpportunity(opportunity)}</dd>
            </div>
          )}
          {minTicket > 0 && (
            <div className='invest-v2__recap-row'>
              <dt>Min. Investment</dt>
              <dd>${convertNumberWithCommas(minTicket.toString())}</dd>
            </div>
          )}
          {hasInterest && (
            <div className='invest-v2__recap-row'>
              <dt>Interest</dt>
              <dd>{formatInterestPercent(opportunity.irr)}</dd>
            </div>
          )}
        </dl>
        <div className='invest-v2__recap-footer'>
          <span className='invest-v2__recap-footer-label'>Investment Amount</span>
          <span
            className={
              hasCommittedAmount
                ? 'invest-v2__recap-footer-value--accent'
                : 'invest-v2__recap-footer-value--pending'
            }
          >
            {hasCommittedAmount ? `$${displayAmount}` : 'Pending'}
          </span>
        </div>
      </div>
      <div
        className={`invest-v2__info-banner ${
          variant === 'amount' ? 'invest-v2__info-banner--trust' : ''
        } ${
          variant === 'review' || variant === 'sign' || variant === 'fund'
            ? 'invest-v2__info-banner--warm'
            : ''
        }`}
        role='status'
      >
        {variant === 'amount' ? (
          <>
            <SecureEnvironmentIcon />
            <p className='invest-v2__info-banner-text'>{infoMessage()}</p>
          </>
        ) : (
          <p className='invest-v2__info-banner-text'>{infoMessage()}</p>
        )}
      </div>
    </aside>
  )
}

export { InvestV2DealRecap }
